import React, { useState } from 'react';
import { Plus, Trash2, Play, Loader2 } from 'lucide-react';
import { ACTIONS } from './constants';

// Non-technical view of the groups (vessels). Each row gets its own Sync
// button so ops can re-pull a single vessel without re-running the whole
// client. Inline edit of value+action; bulk-paste textbox sits below the
// table for fast onboarding.

const VesselsTable = ({ vessels, onChange, onSyncOne, savedDisabled }) => {
  const [busy, setBusy] = useState(null);
  const addRow = () => onChange([...vessels, { value: '', action: 'IMPORT' }]);
  const removeRow = (i) => onChange(vessels.filter((_, idx) => idx !== i));
  const updateRow = (i, patch) =>
    onChange(vessels.map((v, idx) => (idx === i ? { ...v, ...patch } : v)));

  const handleSync = async (value) => {
    setBusy(value);
    try {
      await onSyncOne(value);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="tw-space-y-2">
      {vessels.length === 0 && (
        <div className="tw-p-6 tw-bg-gray-50 tw-rounded-lg tw-text-sm tw-text-gray-500 tw-text-center">
          No vessels configured yet. Click <strong>Add vessel</strong> to start.
        </div>
      )}
      {vessels.length > 0 && (
        <>
          <div className="tw-grid tw-grid-cols-[1fr_160px_120px_40px] tw-gap-2 tw-text-[11px] tw-uppercase tw-text-gray-400 tw-font-semibold tw-px-1 tw-tracking-wider">
            <span>Vessel name</span>
            <span>What to do</span>
            <span />
            <span />
          </div>
          {vessels.map((g, i) => (
            <div
              key={i}
              className="tw-grid tw-grid-cols-[1fr_160px_120px_40px] tw-gap-2 tw-items-center"
            >
              <input
                type="text"
                value={g.value}
                onChange={(e) => updateRow(i, { value: e.target.value.toUpperCase() })}
                placeholder="e.g. ADELAIDE"
                className="tw-w-full tw-px-3 tw-py-2 tw-text-sm tw-border tw-border-gray-200 tw-rounded-lg focus:tw-border-indigo-500 focus:tw-outline-none"
              />
              <select
                value={g.action}
                onChange={(e) => updateRow(i, { action: e.target.value })}
                className="tw-px-3 tw-py-2 tw-text-sm tw-border tw-border-gray-200 tw-rounded-lg tw-bg-white"
              >
                {ACTIONS.map((a) => (
                  <option key={a} value={a}>
                    {actionLabel(a)}
                  </option>
                ))}
              </select>
              <button
                type="button"
                disabled={savedDisabled || !g.value || busy === g.value}
                onClick={() => handleSync(g.value)}
                title={
                  savedDisabled
                    ? 'Save the configuration first'
                    : `Sync only ${g.value} now`
                }
                className="tw-flex tw-items-center tw-justify-center tw-gap-1 tw-px-2 tw-py-2 tw-text-xs tw-font-medium tw-text-indigo-600 tw-bg-indigo-50 tw-rounded-lg tw-border-0 tw-cursor-pointer disabled:tw-opacity-50 disabled:tw-cursor-not-allowed hover:tw-bg-indigo-100"
              >
                {busy === g.value ? (
                  <Loader2 className="tw-w-3.5 tw-h-3.5 tw-animate-spin" />
                ) : (
                  <Play className="tw-w-3.5 tw-h-3.5" />
                )}
                Sync
              </button>
              <button
                type="button"
                onClick={() => removeRow(i)}
                className="tw-p-2 tw-text-gray-400 hover:tw-text-red-500 tw-bg-transparent tw-border-0 tw-cursor-pointer"
                aria-label="Remove vessel"
              >
                <Trash2 className="tw-w-4 tw-h-4" />
              </button>
            </div>
          ))}
        </>
      )}
      <button
        type="button"
        onClick={addRow}
        className="tw-flex tw-items-center tw-gap-2 tw-px-3 tw-py-2 tw-text-sm tw-text-indigo-600 tw-bg-indigo-50 tw-border-0 tw-rounded-lg tw-cursor-pointer hover:tw-bg-indigo-100"
      >
        <Plus className="tw-w-4 tw-h-4" /> Add vessel
      </button>
    </div>
  );
};

const actionLabel = (a) => {
  if (a === 'IMPORT') return 'Import';
  if (a === 'DISABLE') return 'Remove all';
  return 'Skip';
};

export default VesselsTable;
